// Turns a loaded job card (getCurretnCard) into what the invoice form holds, so
// that creating an invoice from a card carries the card's data across unchanged.

// Older cards stored the literal text "undefined" (or "null") in their fields.
function clean(value) {
    if (value === null || value === undefined) return '';
    const text = String(value).trim();
    return ['undefined', 'null'].includes(text.toLowerCase()) ? '' : text;
}

function firstFilled(...values) {
    for (const value of values) {
        const text = clean(value);
        if (text !== '') return text;
    }
    return '';
}

function blankCar() {
    return {c1: '', c2: '', c3: '', text: '', plate_number: '', color: '', model: '', made_year: '',
        workdes: '', vin: '', engine_meter: '', next_oil_change: '', brand: ''};
}

// The save rebuilds plate_number from these parts whenever they are not empty,
// so a missing part has to be '' rather than undefined, or a plate with fewer
// than four parts would be saved with the word "undefined" in it.
function plateParts(plate) {
    const parts = clean(plate).split(/\s+/).filter(Boolean);
    return {c1: parts[0] || '', c2: parts[1] || '', c3: parts[2] || '', text: parts.slice(3).join(' ')};
}

// Each car keeps everything stored on the card. A field the card left empty falls
// back to the same plate's record in the cars table (card._cars).
export function jobCardInvoiceCars(card) {
    const catalogue = Array.isArray(card._cars) ? card._cars : [];
    const stored = Array.isArray(card.carsjson) ? card.carsjson : [];
    const source = stored.length ? stored : catalogue;
    if (!source.length) return [blankCar()];
    return source.map((car) => {
        const plate = clean(car.plate_number);
        const known = catalogue.find((item) => clean(item.plate_number) === plate) || {};
        const copied = {};
        for (const key of Object.keys(car)) copied[key] = clean(car[key]);
        return {
            ...blankCar(),
            ...copied,
            ...plateParts(plate),
            plate_number: plate,
            brand: firstFilled(car.brand, known.brand),
            model: firstFilled(car.model, known.model),
            made_year: firstFilled(car.made_year, car.manufacturing_year, known.made_year, known.manufacturing_year),
            color: firstFilled(car.color, known.color),
            vin: firstFilled(car.vin, known.vin),
            engine_meter: firstFilled(car.engine_meter, known.engine_meter),
        };
    });
}

function customerType(card) {
    const client = card.client || {};
    return Number(card.customer_type) || Number(client.customer_type) || 1;
}

// A new object for every conversion: a company left over from the last card this
// form prepared must not end up on this card's invoice.
export function jobCardInvoiceCompany(card) {
    const client = card.client || {};
    const company = card.company || {};
    const isIndividual = customerType(card) === 1;
    const identity = (...values) => (isIndividual ? '' : firstFilled(...values));
    return {
        fullname: identity(client.company_name, company.fullname),
        vatid: identity(company.vatid, client.company_vatid),
        projectname: identity(company.projectname, client.projectname),
        building_no: identity(company.building_no, client.building_no),
        street_name: identity(company.street_name, client.street_name),
        branch_number: identity(company.branch_number, client.branch_number),
        district: identity(company.district, client.district),
        zipcode: identity(company.zipcode, client.zipcode),
        city: identity(company.city, client.city),
        address: identity(company.address, client.address),
        crt_number: identity(company.crt_number, client.crt_number),
        entity_name: identity(company.entity_name, client.entity_name),
        branch_name: identity(company.branch_name, client.branch_name),
        entity_branch_number: '',
        request_number: clean(card.request_number),
        order_number: clean(card.order_number),
        po_number: clean(card.po_number),
        po_date: poDateParts(card.po_date).year ? clean(card.po_date).substring(0, 10) : '',
        old_invoice: clean(card.old_invoice),
    };
}

// The form edits the PO date through three selects and copies them over po_date
// when saving, so they have to hold the card's date too.
export function poDateParts(value) {
    const match = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(clean(value));
    if (!match || !Number(match[1]) || !Number(match[2]) || !Number(match[3])) return {day: '', month: '', year: ''};
    return {day: Number(match[3]), month: Number(match[2]), year: Number(match[1])};
}

export function jobCardInvoiceData(card) {
    const client = card.client || {};
    const type = customerType(card);
    const cars = jobCardInvoiceCars(card);
    return {
        cars,
        company: jobCardInvoiceCompany(card),
        poDate: poDateParts(card.po_date),
        customerType: type,
        contractId: Number(card.contract_id) || 0,
        customer: {
            id_number: '',
            full_name: '',
            ...client,
            id: client.id || card.customerid || 0,
            // The form's customer lookup replaces the customer and company, clearing
            // the PO fields, unless this is filled. The card already says who it is.
            customerid: firstFilled(client.customerid, client.id, card.customerid),
            mobile: firstFilled(card.mobile, client.mobile),
            cctype: type,
        },
        invoice: {
            work_type: Number(card.work_type) || 1,
            card_type: Number(card.card_type_id) || 1,
            discount: card.discount,
            customer_name: card.customer_name,
            customer_afforded: card.customer_afforded,
            owner_name: clean(card.owner_name),
            notes: clean(card.notes),
            carsnumber: cars.length,
        },
    };
}
